'use client';

import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Star, GripVertical, DraftingCompass } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { Typography } from '@/shared/ui/shadcn/typography';
import { ProjectAngleMainBadge } from './ProjectAngleMainBadge';
import {
  getProjectAngleDisplayName,
  type ProjectAngle,
} from '@/types/project-angle';
import { cn } from '@/shared/lib/cn';

interface AngleSelectionRowProps {
  angle: ProjectAngle;
  index: number;
  isSelected: boolean;
  selectionIndex: number | null;
  disabled?: boolean;
  onToggle: () => void;
  sortable?: boolean;
  /**
   * When provided, replaces the displayed angle title with a positional label
   * (e.g. "Main angle", "Second angle"). Used by the concept-angles modal for
   * selected rows where the slot position conveys the meaning. Suppresses the
   * main badge since position 1 already reads as "main".
   */
  positionLabel?: string;
  /**
   * Minimal text mode (concept-creation sheet): hide the angle name and
   * description so the row is just badge + thumbnail. Only a supplied
   * `positionLabel` (e.g. "Main view" on the main row) is shown. Keeps the
   * creation sheet text-light so first-time users orient by thumbnail.
   */
  compact?: boolean;
}

export function AngleSelectionRow({
  angle,
  index,
  isSelected,
  selectionIndex,
  disabled = false,
  onToggle,
  sortable = false,
  positionLabel,
  compact = false,
}: AngleSelectionRowProps) {
  const t = useTranslations('angles');
  const displayName = getProjectAngleDisplayName(angle, index);
  // Title shown in the row body: a positional label when supplied; otherwise the
  // angle name — except in compact mode, where unlabeled rows stay text-free.
  const title = positionLabel ?? (compact ? undefined : displayName);
  const showDescription = !compact && !!angle.attributes.description;
  const isMain = selectionIndex === 0;
  // When a positional label is supplied, the label itself already conveys
  // "main", so don't also render the badge.
  const showMainBadge = isMain && !positionLabel;

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: angle.id,
    disabled: !sortable,
  });

  const style = sortable
    ? {
        transform: CSS.Transform.toString(transform),
        transition,
      }
    : undefined;

  return (
    <div
      ref={sortable ? setNodeRef : undefined}
      style={style}
      className={cn(
        'flex items-center gap-3 py-2 px-2 rounded-lg transition-all cursor-pointer',
        'hover:bg-muted/50',
        isDragging && 'shadow-lg border-border z-50 opacity-90 bg-background',
        disabled && 'opacity-50 cursor-not-allowed'
      )}
      onClick={() => !disabled && onToggle()}
      role="button"
      tabIndex={disabled ? -1 : 0}
      onKeyDown={(e) => {
        if ((e.key === 'Enter' || e.key === ' ') && !disabled) {
          e.preventDefault();
          onToggle();
        }
      }}
    >
      {/* Selection Badge */}
      <div className="flex-shrink-0">
        {isSelected ? (
          <div
            className={cn(
              'w-7 h-7 rounded-full flex items-center justify-center',
              'bg-primary text-primary-foreground'
            )}
          >
            {isMain ? (
              <Star className="w-3.5 h-3.5 fill-current" />
            ) : (
              <span className="text-chip">
                {(selectionIndex ?? 0) + 1}
              </span>
            )}
          </div>
        ) : (
          <div
            className={cn(
              'w-7 h-7 rounded-full border-2',
              disabled
                ? 'border-muted-foreground/20'
                : 'border-muted-foreground/50'
            )}
          />
        )}
      </div>

      {/* Thumbnail */}
      <div
        className={cn(
          'relative w-[56px] h-[56px] flex-shrink-0 rounded-md overflow-hidden bg-muted',
          disabled && 'opacity-50'
        )}
      >
        {angle.attributes.file_url ? (
          <img
            src={angle.attributes.file_url}
            alt={displayName}
            className="w-full h-full object-cover"
          />
        ) : angle.attributes.has_source_image === false ? (
          // Constructed view — it was never photographed, so "No image" would
          // read as a failed upload. The compass says it is a camera, not a gap.
          <div className="w-full h-full flex items-center justify-center text-muted-foreground">
            <DraftingCompass className="h-5 w-5" />
          </div>
        ) : (
          <div className="w-full h-full flex items-center justify-center text-muted-foreground">
            <span className="text-card-meta">No image</span>
          </div>
        )}
      </div>

      {/* Content — kept as a flex spacer even when text-free (compact mode) so
          the drag handle stays right-aligned and row height is consistent. */}
      <div className={cn('flex-1 min-w-0', disabled && 'opacity-50')}>
        {title && (
          <div className="flex items-center gap-2">
            <Typography variant="p" className="font-medium truncate !mt-0">
              {title}
            </Typography>
            {showMainBadge && <ProjectAngleMainBadge />}
          </div>
        )}
        {showDescription && (
          <Typography
            variant="small"
            className="text-muted-foreground truncate mt-0.5"
          >
            {angle.attributes.description!.replace(/\n/g, ' ')}
          </Typography>
        )}
      </div>

      {/* Drag Handle (only for selected sortable items) — right-aligned to match Android/Apple */}
      {sortable && (
        <button
          {...attributes}
          {...listeners}
          className="touch-none p-1 text-muted-foreground hover:text-foreground cursor-grab active:cursor-grabbing flex-shrink-0"
          aria-label={t('reorderHandle')}
          onClick={(e) => e.stopPropagation()}
        >
          <GripVertical className="w-5 h-5" />
        </button>
      )}
    </div>
  );
}
